<script lang="ts">
	import type { PageData } from './$types';
	import type { StateData, RepeaterResult } from '$lib/types/repeater';
	import RepeaterModal from '$lib/components/RepeaterModal.svelte';
	import { Loader2, AlertCircle } from 'lucide-svelte';

	export let data: PageData;
	$: repeaters = data.repeaters as StateData;
	$: loading = data.loading;
	$: error = data.error;
	$: progress = data.progress;

	let selectedRepeater: RepeaterResult | null = null;
	let showModal = false;

	function openModal(repeater: RepeaterResult) {
		selectedRepeater = repeater;
		showModal = true;
	}

	function getModes(repeater: RepeaterResult): string {
		const modes = [
			{ key: 'FM Analog', display: 'FM' },
			{ key: 'DMR', display: 'DMR' },
			{ key: 'D-Star', display: 'D-Star' },
			{ key: 'NXDN', display: 'NXDN' },
			{ key: 'APCO P-25', display: 'P25' },
			{ key: 'M17', display: 'M17' },
			{ key: 'Tetra', display: 'Tetra' },
			{ key: 'System Fusion', display: 'YSF' }
		];

		const activeModes = modes
			.filter((mode) => repeater[mode.key] === 'Yes')
			.map((mode) => mode.display);

		return activeModes.length > 0 ? activeModes.join(', ') : '-';
	}
</script>

<div class="container mx-auto px-4 py-8">
	<h1 class="mb-8 text-3xl font-bold">Local Repeater List</h1>

	<div
		class="mb-8 rounded-lg bg-blue-50 p-4 text-sm text-blue-800 dark:bg-blue-900/50 dark:text-blue-300"
	>
		<p>
			This repeater information is provided by <a
				href="https://www.repeaterbook.com"
				target="_blank"
				rel="noopener noreferrer"
				class="font-medium underline hover:text-blue-600 dark:hover:text-blue-400">RepeaterBook</a
			>. If you notice any information that needs to be updated, you can access the RepeaterBook
			update system through the "More" button for each repeater.
		</p>
	</div>

	{#if loading}
		<div class="flex min-h-[200px] items-center justify-center">
			<div class="text-center">
				<Loader2 class="mb-4 h-8 w-8 animate-spin text-blue-500" />
				<p class="text-sm text-gray-600 dark:text-gray-400">
					Loading the most up-to-date repeater information...
					{#if progress}
						<br />
						<span class="mt-2 block text-xs">
							Loading {progress.state}
							{progress.county} County ({progress.current}/{progress.total} counties)
						</span>
					{/if}
				</p>
			</div>
		</div>
	{:else if error}
		<div class="flex min-h-[200px] items-center justify-center">
			<div class="text-center">
				<AlertCircle class="mx-auto mb-4 h-8 w-8 text-red-500" />
				<p class="text-sm text-red-600 dark:text-red-400">
					{error}
				</p>
				<button
					class="mt-4 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
					on:click={() => window.location.reload()}
				>
					Try Again
				</button>
			</div>
		</div>
	{:else}
		{#each Object.entries(repeaters) as [state, counties]}
			<div class="mb-8">
				<h2 class="mb-4 text-2xl font-semibold">{state}</h2>
				{#each Object.entries(counties) as [county, repeaterList]}
					{#if repeaterList && repeaterList.length > 0}
						<div class="mb-6">
							<h3 class="mb-2 text-xl font-medium">{county} County</h3>
							<div class="overflow-x-auto">
								<table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
									<thead class="bg-gray-50 dark:bg-gray-800">
										<tr>
											<th
												class="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400"
												>Callsign</th
											>
											<th
												class="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400"
												>RX Freq</th
											>
											<th
												class="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400"
												>TX Freq</th
											>
											<th
												class="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400"
												>Tone</th
											>
											<th
												class="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400"
												>Color Code</th
											>
											<th
												class="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400"
												>Modes</th
											>
											<th
												class="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400"
												>Location</th
											>
											<th
												class="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400"
												>Use</th
											>
											<th
												class="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400"
												>Status</th
											>
											<th
												class="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400"
												>More</th
											>
										</tr>
									</thead>
									<tbody
										class="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900"
									>
										{#each repeaterList as repeater}
											<tr class="hover:bg-gray-50 dark:hover:bg-gray-800">
												<td
													class="px-6 py-4 text-sm font-medium whitespace-nowrap text-gray-900 dark:text-white"
												>
													{repeater.Callsign || '-'}
												</td>
												<td
													class="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400"
												>
													{repeater.Frequency || '-'}
												</td>
												<td
													class="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400"
												>
													{repeater['Input Freq'] || '-'}
												</td>
												<td
													class="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400"
												>
													{repeater.PL || '-'}
												</td>
												<td
													class="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400"
												>
													{repeater['DMR Color Code'] || '-'}
												</td>
												<td
													class="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400"
												>
													{getModes(repeater)}
												</td>
												<td
													class="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400"
												>
													{repeater.Landmark || '-'}
												</td>
												<td
													class="px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400"
												>
													{repeater.Use || '-'}
												</td>
												<td class="px-6 py-4 text-sm whitespace-nowrap">
													<span
														class={repeater['Operational Status'] === 'On-air'
															? 'text-green-600 dark:text-green-400'
															: 'text-red-600 dark:text-red-400'}
													>
														{repeater['Operational Status'] || '-'}
													</span>
												</td>
												<td class="px-6 py-4 text-sm whitespace-nowrap">
													<button
														class="rounded bg-blue-500 px-3 py-1 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
														on:click={() => openModal(repeater)}
													>
														More
													</button>
												</td>
											</tr>
										{/each}
									</tbody>
								</table>
							</div>
						</div>
					{/if}
				{/each}
			</div>
		{/each}
	{/if}
</div>

{#if selectedRepeater && showModal}
	<RepeaterModal repeater={selectedRepeater} bind:showModal />
{/if}
